import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  StickyNote,
  Users,
  Calendar,
  Phone,
  Bell
} from 'lucide-react';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { useDashboardHeaderTabs } from './DashboardHeaderContext';
import { NotesSystem } from '../admin/NotesSystem';
import { BulletinsBoard } from '../moderation/BulletinsBoard';
import { ProviderSelection } from '../providers/ProviderSelection';
import { AppointmentDetails } from '../providers/AppointmentDetails';
import { ProviderPool } from '../providers/ProviderPool';

type TabId = 'home' | 'notes' | 'providers' | 'appointments' | 'contacts';
type Tab = {
  id: TabId;
  label: string;
  icon: any;
  color: string;
  bgColor: string;
};

interface MainApplicationDashboardProps {
  
}

export const MainApplicationDashboard: React.FC<MainApplicationDashboardProps> = () => {
  const { settings } = useApplicationSettings();
  const setHeaderTabs = useDashboardHeaderTabs();
  const [activeTab, setActiveTab] = useState<TabId>('providers');

  // Filter tabs based on feature settings - only show if settings are loaded
  const tabs: Tab[] = settings ? [
    settings.bulletinsEnabled ? { id: 'home', label: 'Bulletins', icon: Bell, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab : null,
    settings.notesEnabled ? { id: 'notes', label: 'Notes', icon: StickyNote, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab : null,
    { id: 'providers', label: 'Provider Selection', icon: Users, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
    { id: 'appointments', label: 'Appointment Details', icon: Calendar, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
    { id: 'contacts', label: 'Provider Pool Contact', icon: Phone, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
  ].filter((t): t is Tab => Boolean(t)) : [
    // Default tabs when settings are loading (only essential ones)
    { id: 'providers', label: 'Provider Selection', icon: Users, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
    { id: 'appointments', label: 'Appointment Details', icon: Calendar, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
    { id: 'contacts', label: 'Provider Pool Contact', icon: Phone, color: 'var(--color-accent)', bgColor: 'var(--color-bg-tertiary)' } as Tab,
  ];

  // Handle case where current tab becomes unavailable or when settings change
  React.useEffect(() => {
    const currentTabExists = tabs.some((tab) => tab.id === activeTab);
    if (!currentTabExists) {
      // Switch to first available tab, prefer providers
      const firstTab = tabs.find(tab => tab.id === 'providers') || tabs[0];
      setActiveTab((firstTab?.id as TabId) || 'providers');
    }
  }, [tabs, activeTab]);

  // Reset to providers tab when settings first load
  React.useEffect(() => {
    if (settings && activeTab === 'home' && !settings.bulletinsEnabled) {
      setActiveTab('providers');
    }
    if (settings && activeTab === 'notes' && !settings.notesEnabled) {
      setActiveTab('providers');
    }
  }, [settings, activeTab]);

  // Register tab bar with Layout header so title and tabs share one aligned block
  useEffect(() => {
    if (!setHeaderTabs) return;
    const tabBar = (
      <>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabId)}
              className="relative flex items-center space-x-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap group overflow-hidden"
              style={{
                color: isActive ? tab.color : 'var(--color-text-secondary)',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }
              }}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 rounded-lg"
                  style={{
                    backgroundColor: tab.bgColor,
                    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12)',
                    zIndex: 0
                  }}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <Icon 
                className="w-4 h-4 transition-transform relative z-10" 
                style={{
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                  filter: isActive ? 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' : 'none'
                }}
              />
              <span className="relative z-10" style={{ fontWeight: isActive ? 600 : 500 }}>{tab.label}</span>
              {isActive && (
                <motion.div
                  layoutId="activeTabIndicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                  style={{
                    backgroundColor: tab.color,
                    boxShadow: `0 0 8px ${tab.color}40`,
                    zIndex: 1
                  }}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
            </button>
          );
        })}
      </>
    );
    setHeaderTabs(tabBar);
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, tabs, activeTab]);

  const renderContent = () => {
    // Show loading while settings are being fetched
    if (!settings) {
      return <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading settings...</span>
      </div>;
    }

    switch (activeTab) {
      case 'home':
        return settings.bulletinsEnabled ? <BulletinsBoard readOnly={true} /> : <div>Bulletins feature is disabled</div>;
      case 'notes':
        return settings.notesEnabled ? <NotesSystem /> : <div>Notes feature is disabled</div>;
      case 'providers':
        return <ProviderSelection />;
      case 'appointments':
        return <AppointmentDetails />;
      case 'contacts':
        return <ProviderPool />;
      default:
        return settings.bulletinsEnabled ? <BulletinsBoard readOnly={true} /> : <ProviderSelection />;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      {/* Main Content - tabs are rendered in Layout header via useDashboardHeaderTabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderContent()}
        </motion.div>
      </div>
    </div>
  );
};
